import { isPostHogConfigured, posthog } from '@lib/posthog';
import { enqueuePendingAnalyticsEvent } from '@lib/analytics/pending-events';

/**
 * Central registry of PostHog product analytics events (FFH-2371).
 *
 * Every event name and property shape the web app can capture lives here, and
 * `captureAnalyticsEvent` is the only sanctioned way to send one. Never call
 * `posthog.capture()` at a call site — raw captures bypass the tracking plan
 * and are rejected at PR review, the same discipline `PostHogFeatureFlags`
 * applies to flag keys.
 *
 * The tracking plan (triggers, tiers, the question each event answers, and
 * the product owner's review status) is the source of truth for WHY an event
 * exists; this module is the source of truth for its name and property types.
 * Tracking plan: https://scoreline.atlassian.net/wiki/spaces/FFH/pages/174522383
 *
 * Naming contract, shared with the mobile app:
 * - Wire names are `snake_case`, `noun_verb_past_tense` (`team_saved`,
 *   `team_optimised`, `article_opened`). Web and app names change together
 *   or not at all — the point is cross-platform comparison.
 * - Boolean properties start with an auxiliary verb (`is`, `has`, `was`...).
 * - No PII. Emails and FPL IDs stay out of properties; the member id built
 *   from `profileId` is the identity key and is applied via
 *   `posthog.identify`, not properties.
 * - Never duplicate feature flag state into properties: posthog-js already
 *   stamps active flags on every captured event as `$feature/<key>`, which is
 *   what experiment breakdowns read.
 *
 * Most events here are defined ahead of use: only the team import events fire
 * today. Tier 1 (acquisition) ships with FFH-2372, tier 2 (engagement) with
 * FFH-2373, tier 3 (friction) with FFH-2374. Pageviews are deliberately
 * absent — they are PostHog-native `$pageview` events, owned by FFH-2370.
 */
export enum AnalyticsEvents {
  // Live today — the team screenshot import (see lib/api/import-team.ts).
  // Its started and failed events were dropped in FFH-3561: failures go to
  // Datadog RUM.
  teamImportCompleted = 'team_import_completed',
  // Live today — the funnel's rating call (see GytrProvider).
  teamRatingFailed = 'team_rating_failed',

  // Tier 1: acquisition and monetisation (FFH-2372).
  funnelStarted = 'funnel_started',
  funnelStepCompleted = 'funnel_step_completed',
  funnelEmailCaptured = 'funnel_email_captured',
  teamRatingViewed = 'team_rating_viewed',
  paywallViewed = 'paywall_viewed',
  upgradeCtaClicked = 'upgrade_cta_clicked',
  checkoutStarted = 'checkout_started',
  checkoutFailed = 'checkout_failed',
  purchaseCompleted = 'purchase_completed',
  trialStarted = 'trial_started',
  subscriptionCancelStarted = 'subscription_cancel_started',

  // The logged-out homepage's free routes in (FFH-3375). The device is not a
  // property: posthog-js stamps $device_type on every event already.
  homepageCtaClicked = 'homepage_cta_clicked',
  appBadgeClicked = 'app_badge_clicked',
  appQrViewed = 'app_qr_viewed',
  faqOpened = 'faq_opened',

  // Tier 2: product engagement (FFH-2373). Custom events exist only for what
  // page tracking cannot see: popups, in-page actions and flows. Feature
  // pages themselves are measured by FFH-2370's pageviews.
  playerInfoOpened = 'player_info_opened',
  playerComparisonRun = 'player_comparison_run',
  fixtureTickerFiltered = 'fixture_ticker_filtered',
  featureLockedHit = 'feature_locked_hit',
  captaincyPollVoted = 'captaincy_poll_voted',

  // The 2026 tracking plan's P0 value signals (FFH-3559). The app sends the
  // same names and properties.
  teamOptimised = 'team_optimised',
  aiTransferViewed = 'ai_transfer_viewed',
  aiTransferApplied = 'ai_transfer_applied'
}

/** Property value for membership tier, lowercase by contract (FFH-2371). */
export type AnalyticsMembershipTier = 'free' | 'starter' | 'pro' | 'ultra';

const ANALYTICS_MEMBERSHIP_TIERS: readonly string[] = [
  'free',
  'starter',
  'pro',
  'ultra'
];

/**
 * Normalise a plan title, plan-key segment or CleverTap tier name ('Ultra',
 * 'pro_yearly'.split('_')[0], 'Free plan') to the lowercase analytics
 * vocabulary, or null when it names no tier. Null beats a guessed value:
 * tier is a breakdown dimension, and a wrong one poisons the insight.
 */
export const toAnalyticsMembershipTier = (
  value: string | null | undefined
): AnalyticsMembershipTier | null => {
  if (!value) return null;
  const normalised = value.trim().toLowerCase();
  return ANALYTICS_MEMBERSHIP_TIERS.includes(normalised)
    ? (normalised as AnalyticsMembershipTier)
    : null;
};

/** The two acquisition funnels: get-your-team-rating and mini-league readiness. */
export type AnalyticsFunnel = 'team_rating' | 'mini_league_readiness';

export type AnalyticsBillingPeriod = 'monthly' | 'annual';

export type TeamImportMethod = 'screenshot';

export type AnalyticsAppStore = 'app_store' | 'google_play';

/**
 * The app's two entry points to Optimise. Web has no overflow menu, so it
 * only ever sends `action_button`.
 */
export type TeamOptimisedTrigger = 'action_button' | 'overflow_menu';

/** The AI Transfers tab a suggestion set came from, on web and app alike. */
export type AiTransferSource = 'recommended' | 'custom';

export type TeamImportFailureStage =
  | 'network'
  | 'client_error'
  | 'server_error';

/**
 * The property shape contract, one entry per event. Adding an event means
 * adding it to the tracking plan first, then here, then building the trigger.
 */
export type AnalyticsEventPropertiesMap = {
  [AnalyticsEvents.teamImportCompleted]: {
    import_method: TeamImportMethod;
  };
  /**
   * A rating that did not come back (FFH-2743). Shaped like its import sibling
   * so the two read the same in an insight. `attempt` counts from 1 and is what
   * makes a session that failed repeatedly queryable as such.
   */
  [AnalyticsEvents.teamRatingFailed]: {
    error_message: string;
    failure_kind: string;
    status: number | null;
    attempt: number;
  };

  [AnalyticsEvents.funnelStarted]: {
    funnel: AnalyticsFunnel;
    entry_route: string;
  };
  [AnalyticsEvents.funnelStepCompleted]: {
    funnel: AnalyticsFunnel;
    step_index: number;
    step_name: string;
  };
  [AnalyticsEvents.funnelEmailCaptured]: {
    funnel: AnalyticsFunnel;
  };
  [AnalyticsEvents.teamRatingViewed]: {
    rating: number;
    // Null when the transfer solve was deferred (SUGGESTION_PENDING): the
    // reveal must not wait for it, so the potential is honestly unknown at
    // view time rather than guessed (FFH-2372).
    rating_potential: number | null;
  };
  [AnalyticsEvents.paywallViewed]: {
    placement: string;
    feature: string;
    // FFH-3570: the paid feature that opened the paywall, the name the app
    // shares. See toPaywallViewedProperties for why it repeats `feature`.
    trigger_feature: string;
    // FFH-3570: the route pattern the paywall opened on, as the pageview's
    // `route` carries it; a web page is the app's tab. Null only where no
    // Next router is mounted, which a page never is.
    origin_tab: string | null;
  };
  [AnalyticsEvents.upgradeCtaClicked]: {
    placement: string;
    // Null for CTAs that point at /join generically (padlocks, the site
    // paywall) rather than at a specific plan (FFH-2372).
    intended_tier: AnalyticsMembershipTier | null;
  };
  [AnalyticsEvents.checkoutStarted]: {
    // Null when the CTA's plan title resolves to no known tier; defensive
    // only, the join surfaces all use the canonical names (FFH-2372).
    intended_tier: AnalyticsMembershipTier | null;
    billing_period: AnalyticsBillingPeriod;
    coupon: string | null;
  };
  [AnalyticsEvents.checkoutFailed]: {
    reason: string;
    http_status: number | null;
  };
  [AnalyticsEvents.purchaseCompleted]: {
    // Null only for a plan id outside the sellable map; defensive, so an
    // exotic plan still counts as a purchase rather than being dropped.
    tier: AnalyticsMembershipTier | null;
    billing_period: AnalyticsBillingPeriod;
    value: number;
    currency: string;
  };
  [AnalyticsEvents.trialStarted]: {
    tier: AnalyticsMembershipTier | null;
  };
  [AnalyticsEvents.subscriptionCancelStarted]: {
    tier: AnalyticsMembershipTier;
    // Null until the account API exposes a subscription start date: nothing
    // client-side knows when the subscription began (FFH-2372).
    tenure_days: number | null;
  };
  [AnalyticsEvents.homepageCtaClicked]: {
    placement: string;
    href: string;
  };
  [AnalyticsEvents.appBadgeClicked]: {
    placement: string;
    store: AnalyticsAppStore;
  };
  [AnalyticsEvents.appQrViewed]: {
    placement: string;
  };
  [AnalyticsEvents.faqOpened]: {
    placement: string;
    question: string;
  };

  [AnalyticsEvents.playerInfoOpened]: {
    source: string;
  };
  [AnalyticsEvents.playerComparisonRun]: {
    player_count: number;
  };
  [AnalyticsEvents.fixtureTickerFiltered]: {
    filter_type:
      | 'type'
      | 'gameweek_range'
      | 'team'
      | 'team_strength'
      | 'hidden_gameweeks';
  };
  [AnalyticsEvents.featureLockedHit]: {
    placement: string;
    feature: string;
  };
  /**
   * The poll's gameweek is the next one, not the current gameweek the super
   * property carries, but the poll only ever runs for the next deadline.
   */
  [AnalyticsEvents.captaincyPollVoted]: {
    placement: string;
    is_change: boolean;
  };

  [AnalyticsEvents.teamOptimised]: {
    trigger: TeamOptimisedTrigger;
  };

  /**
   * A suggestion set shown to the member (FFH-3567). `suggestion_id` is made
   * on the client, since the API returns none, and joins the set to its
   * `ai_transfer_applied`.
   */
  [AnalyticsEvents.aiTransferViewed]: {
    suggestion_id: string;
    transfer_count: number;
    // Free transfers left for the next gameweek. Null when they are unlimited
    // (wildcard, free hit) or could not be read, rather than a made-up 0.
    free_transfers_available: number | null;
    source: AiTransferSource;
  };
  /** A suggestion set saved to the planner (FFH-3567). */
  [AnalyticsEvents.aiTransferApplied]: {
    suggestion_id: string;
    transfer_count: number;
    source: AiTransferSource;
  };
};

/**
 * Capture a product analytics event. The single entry point to
 * `posthog.capture` for the whole app; the generic ties the properties
 * argument to the event's declared shape at compile time.
 *
 * No-ops when PostHog is not configured (local development, e2e runs without
 * the env vars), so call sites never need their own guard. Consent gating is
 * PostHog's own: capture is opted out until `setPostHogConsent(true)`.
 *
 * Events captured before the first consent read (mount-time fires racing the
 * ~500ms CookieYes settle) are parked in the pending queue instead of being
 * silently dropped by the opted-out SDK; `setPostHogConsent` flushes them on
 * grant and discards them on denial (FFH-2372).
 */
export const captureAnalyticsEvent = <EventName extends AnalyticsEvents>(
  eventName: EventName,
  properties: AnalyticsEventPropertiesMap[EventName]
): void => {
  if (typeof window === 'undefined') return;
  if (!isPostHogConfigured()) return;

  if (!posthog.__loaded || !posthog.has_opted_in_capturing()) {
    enqueuePendingAnalyticsEvent(eventName, properties);
    return;
  }

  posthog.capture(eventName, properties);
};
